import RecruitingStageAccordionList from "./RecruitingStageAccordionList";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Checkbox,
  Menu,
  MenuItem,
  Link as MuiLink,
  Typography,
} from "@mui/material";
import { Building, Location, ProfileCircle } from "iconsax-react";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { useNavigate } from "react-router-dom";

import { MoveStageActionIcon } from "../ActionButtonIcons";
import JobTabHeader from "./JobTabHeader";
import RejectedStageDialog from "./RejectedStageDialog";
import RejectionAudit from "./RejectionAudit";
import { AccordionHeadingIcon } from "./StageViewAccordionList";
import { MOVE_STAGE_BULK_BUTTON_SX } from "../stageBulkButtonStyles";
import { buildOpenCompanyNavPayload } from "../../utils/companyNavigation";
import {
  JOB_CANDIDATE_STAT_ITEMS,
  getJobCandidateStatLabel,
  isJobCandidateStatMatch,
  resolveJobCandidateDisplayStageLabel,
} from "../../utils/jobCandidateFilters";
import {
  fetchRecruiterUsers,
  normalizeTeamMemberName,
} from "../../utils/teamMembers";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const BLUE = "#3451F4";
const PURPLE = "#9C4FFF";
const DANGER = "#DC2626";
const TEXT = "#111827";
const MUTED = "#94A3B8";
const NAV_CACHE_KEY = "rpo:openCompanyNav";

function MatchActionButton({ children, color = MUTED, disabled = false, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        border: 0,
        p: 0,
        m: 0,
        backgroundColor: "transparent",
        fontFamily: "Be Vietnam Pro",
        fontSize: 10.5,
        fontWeight: 900,
        color,
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.65 : 1,
      }}
    >
      {children}
    </Box>
  );
}

const DEFAULT_STAGE_ORDER = JOB_CANDIDATE_STAT_ITEMS.map((item) => item.label);
const MOVE_STAGE_OPTIONS = [
  "New",
  "Applied",
  "Matched",
  "Sourced",
  "Contacted",
  "Screened",
  "Presented",
  "Interview",
  "Assessment",
  "Offer",
  "Hired",
  "Rejected",
];
const isAiMatchRow = (row = {}) =>
  String(row?.match_source || "")
    .trim()
    .toLowerCase() !== "manual";

const parseMatchScore = (row = {}) => {
  const directScore = Number(row?.match_score ?? row?.score ?? 0);
  if (Number.isFinite(directScore) && directScore > 0) {
    return Math.max(0, Math.min(100, directScore));
  }

  const labelMatch = String(row?.last_sync_label || "").match(/(\d{1,3})\s*%/);
  if (labelMatch?.[1]) {
    const parsedScore = Number(labelMatch[1]);
    if (Number.isFinite(parsedScore)) {
      return Math.max(0, Math.min(100, parsedScore));
    }
  }

  return 0;
};

const getAuthHeaders = () => {
  const token =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    "";

  return token
    ? {
        Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
        "x-access-token": token.replace(/^Bearer\s+/i, ""),
      }
    : {};
};

const parseSkills = (value) => {
  if (Array.isArray(value)) {
    return value
      .filter(Boolean)
      .map((item) => String(item).trim())
      .filter(Boolean);
  }
  if (typeof value !== "string") return [];

  const trimmed = value.trim();
  if (!trimmed) return [];

  try {
    const parsed = JSON.parse(trimmed);
    return Array.isArray(parsed)
      ? parsed
          .filter(Boolean)
          .map((item) => String(item).trim())
          .filter(Boolean)
      : [];
  } catch {
    return trimmed
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
};

const normalizeMatchRow = (row = {}) => ({
  ...row,
  match_score: parseMatchScore(row),
  match_reason: row?.match_reason || row?.reason || "",
  stage: resolveJobCandidateDisplayStageLabel(row),
  rejectedBy: String(row?.rejected_by || row?.rejectedBy || "").trim(),
  rejectionReason: String(
    row?.rejection_reason || row?.rejectionReason || ""
  ).trim(),
  rejectionNote: String(row?.rejection_note || row?.rejectionNote || "").trim(),
});

const normalizeLookupName = (value) =>
  normalizeTeamMemberName(value).toLowerCase();

const getRecruiterProfileId = (recruiterUsersByName, recruiterName) => {
  const lookupKey = normalizeLookupName(recruiterName);
  if (!lookupKey || typeof recruiterUsersByName?.get !== "function") return null;

  const matchedRecruiter = recruiterUsersByName.get(lookupKey);
  const numericId = Number(matchedRecruiter?.id || 0);
  return Number.isFinite(numericId) && numericId > 0 ? numericId : null;
};

function StageTextLink({ children, onClick, sx = {} }) {
  return (
    <MuiLink
      component="button"
      type="button"
      underline="hover"
      onClick={onClick}
      sx={{
        p: 0,
        border: 0,
        backgroundColor: "transparent",
        color: "inherit",
        fontSize: "inherit",
        fontWeight: "inherit",
        fontFamily: "inherit",
        lineHeight: "inherit",
        textAlign: "left",
        cursor: "pointer",
        textUnderlineOffset: "2px",
        minWidth: 0,
        verticalAlign: "baseline",
        "&:hover": {
          color: "inherit",
        },
        ...sx,
      }}
    >
      {children}
    </MuiLink>
  );
}

function RejectionDetails({ item, compact = false }) {
  if (item.outcome === "Rejected" && item.rejected_at) return <RejectionAudit item={item} />;
  const stageLabel = resolveJobCandidateDisplayStageLabel(item);
  if (stageLabel !== "Rejected") return null;

  const detailParts = [
    item.rejectedBy ? `Rejected by: ${item.rejectedBy}` : "",
    item.rejectionReason ? `Reason: ${item.rejectionReason}` : "",
  ].filter(Boolean);

  if (!detailParts.length && !item.rejectionNote) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: compact ? 0.7 : 1,
        px: compact ? 0.9 : 1,
        py: compact ? 0.75 : 0.9,
        borderRadius: 1.5,
        border: "1px solid #FECACA",
        backgroundColor: "#FEF2F2",
      }}
    >
      {detailParts.length ? (
        <Typography
          sx={{
            fontFamily: "Be Vietnam Pro",
            fontSize: compact ? 10 : 10.5,
            fontWeight: 900,
            color: DANGER,
            lineHeight: 1.35,
          }}
        >
          {detailParts.join(" • ")}
        </Typography>
      ) : null}

      {item.rejectionNote ? (
        <Typography
          sx={{
            mt: detailParts.length ? 0.4 : 0,
            fontFamily: "Be Vietnam Pro",
            fontSize: compact ? 10 : 10.5,
            fontWeight: 800,
            color: "#7F1D1D",
            lineHeight: 1.4,
            whiteSpace: "pre-line",
            wordBreak: "break-word",
          }}
        >
          Note: {item.rejectionNote}
        </Typography>
      ) : null}
    </Box>
  );
}

function MatchCard({
  item,
  checked = false,
  moving = false,
  canDelete = false,
  onToggleSelect,
  onRejectCandidate,
  onDeleteCandidate,
  onOpenCompany,
  onOpenRecruiterProfile,
}) {
  const skills = parseSkills(item.skills_json).slice(0, 5);
  const extraSkills = Math.max(0, Number(item.extra_skills_count || 0));
  const initials =
    String(item.candidate_name || "?")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() || "")
      .join("") || "?";
  const companyName = String(
    item.candidate_company || item.current_company || ""
  ).trim();

  return (
    <Card
      variant="outlined"
      sx={{
        borderColor: "#E6EAF2",
        borderRadius: 2,
        backgroundColor: "#fff",
        overflow: "hidden",
      }}
    >
      <Box sx={{ p: 1.25 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Avatar
            src={item.avatar_url || undefined}
            sx={{
              width: 32,
              height: 32,
              bgcolor: "#EEF2FF",
              color: BLUE,
              fontWeight: 700,
              fontSize: 12,
              flex: "0 0 auto",
            }}
          >
            {initials}
          </Avatar>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: BLUE }} noWrap>
              {item.candidate_name || "Candidate"}
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: 11, fontWeight: 600, color: MUTED }} noWrap>
              {item.candidate_title || "-"}
            </Typography>
          </Box>

          <Chip
            label={`${Math.round(Number(item.match_score || 0))}%`}
            size="small"
            sx={{
              height: 22,
              borderRadius: 999,
              bgcolor: "#EAF2FF",
              color: BLUE,
              fontWeight: 700,
            }}
          />

          <Checkbox
            size="small"
            checked={checked}
            disabled={!item.candidate_id || moving}
            onChange={() => onToggleSelect?.(item.candidate_id)}
            onClick={(event) => event.stopPropagation()}
            sx={{
              p: 0,
              width: 18,
              height: 18,
              mt: 0.15,
              color: "#D0D5DD",
              "& .MuiSvgIcon-root": { fontSize: 18 },
              "&.Mui-checked": { color: PURPLE },
            }}
          />
        </Box>

        <Box sx={{ mt: 1.1, display: "grid", gap: 0.65 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Building size="14" color={MUTED} variant="Outline" />
            <Typography sx={{ fontSize: 11, color: TEXT, fontWeight: 500 }} noWrap>
              Company:{" "}
              {item.candidate_company_id && companyName ? (
                <StageTextLink
                  onClick={() =>
                    onOpenCompany?.(item.candidate_company_id, companyName)
                  }
                  sx={{ color: BLUE, fontWeight: 700 }}
                >
                  {companyName}
                </StageTextLink>
              ) : (
                companyName || "No recent company"
              )}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Location size="14" color={MUTED} variant="Outline" />
            <Typography sx={{ fontSize: 11, color: TEXT, fontWeight: 500 }} noWrap>
              {item.candidate_location || "Location unavailable"}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <ProfileCircle size="14" color={MUTED} variant="Outline" />
            <Typography sx={{ fontSize: 11, color: TEXT, fontWeight: 500 }} noWrap>
              Owner:{" "}
              {item.recruiterProfileId ? (
                <StageTextLink
                  onClick={() =>
                    onOpenRecruiterProfile?.(item.recruiterProfileId)
                  }
                  sx={{ color: BLUE, fontWeight: 700 }}
                >
                  {item.recruiter_name || "Unassigned"}
                </StageTextLink>
              ) : (
                item.recruiter_name || "Unassigned"
              )}
            </Typography>
          </Box>
        </Box>

        {item.match_reason ? (
          <Typography sx={{ mt: 1, fontSize: 11, color: "#475467", lineHeight: 1.45 }}>
            {item.match_reason}
          </Typography>
        ) : null}

        <RejectionDetails item={item} />

        <Box sx={{ mt: 1.05, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.1 }}>
          <MatchActionButton
            disabled={!item.candidate_id || moving}
            onClick={() => onRejectCandidate?.(item.candidate_id)}
          >
            Pass
          </MatchActionButton>
          {canDelete ? (
            <MatchActionButton
              color={DANGER}
              disabled={!item.candidate_id || moving}
              onClick={() => onDeleteCandidate?.(item.candidate_id)}
            >
              Delete
            </MatchActionButton>
          ) : null}
        </Box>

        {skills.length ? (
          <Box sx={{ mt: 1, display: "flex", gap: 0.6, flexWrap: "wrap" }}>
            {skills.map((skill) => (
              <Chip
                key={skill}
                label={skill}
                size="small"
                sx={{
                  height: 20,
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 600,
                  color: BLUE,
                  bgcolor: "#F5F3FF",
                }}
              />
            ))}
            {extraSkills > 0 ? (
              <Chip
                label={`+${extraSkills}`}
                size="small"
                sx={{
                  height: 20,
                  borderRadius: 999,
                  fontSize: 10,
                  fontWeight: 600,
                  color: PURPLE,
                  bgcolor: "#F5F3FF",
                }}
              />
            ) : null}
          </Box>
        ) : null}

      </Box>
    </Card>
  );
}

function MatchRow({
  item,
  checked = false,
  moving = false,
  canDelete = false,
  onToggleSelect,
  onRejectCandidate,
  onDeleteCandidate,
  onOpenCompany,
  onOpenRecruiterProfile,
}) {
  const companyName = String(
    item.candidate_company || item.current_company || ""
  ).trim();
  const recruiterName = String(item.recruiter_name || "").trim();
  const hasTitle = Boolean(item.candidate_title);
  const hasCompany = Boolean(companyName);
  const hasLocation = Boolean(item.candidate_location);
  const hasRecruiter = Boolean(recruiterName);

  return (
    <Box
      sx={{
        border: "1px solid #E6EAF2",
        borderRadius: 2,
        backgroundColor: "#fff",
        px: 1.25,
        py: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.2,
      }}
    >
      <Avatar
        src={item.avatar_url || undefined}
        sx={{
          width: 30,
          height: 30,
          bgcolor: "#EEF2FF",
          color: BLUE,
          fontWeight: 700,
          fontSize: 12,
          flex: "0 0 auto",
        }}
      >
        {String(item.candidate_name || "?")
          .split(/\s+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((part) => part[0]?.toUpperCase() || "")
          .join("") || "?"}
      </Avatar>

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: BLUE }} noWrap>
          {item.candidate_name || "Candidate"}
        </Typography>
        <Typography
          component="div"
          sx={{ mt: 0.25, fontSize: 11, fontWeight: 500, color: MUTED }}
          noWrap
        >
          {hasTitle ? <Box component="span">{item.candidate_title}</Box> : null}
          {hasCompany || hasLocation || hasRecruiter ? (
            hasTitle ? <Box component="span"> | </Box> : null
          ) : null}
          {hasCompany ? (
            item.candidate_company_id ? (
              <StageTextLink
                onClick={() =>
                  onOpenCompany?.(item.candidate_company_id, companyName)
                }
                sx={{ color: BLUE, fontWeight: 700 }}
              >
                {companyName}
              </StageTextLink>
            ) : (
              <Box component="span">{companyName}</Box>
            )
          ) : null}
          {hasLocation || hasRecruiter ? (
            hasCompany ? <Box component="span"> | </Box> : null
          ) : null}
          {hasLocation ? (
            <Box component="span">{item.candidate_location}</Box>
          ) : null}
          {hasRecruiter ? (
            hasLocation || hasCompany ? <Box component="span"> | </Box> : null
          ) : null}
          {hasRecruiter ? (
            item.recruiterProfileId ? (
              <StageTextLink
                onClick={() =>
                  onOpenRecruiterProfile?.(item.recruiterProfileId)
                }
                sx={{ color: BLUE, fontWeight: 700 }}
              >
                {recruiterName}
              </StageTextLink>
            ) : (
              <Box component="span">{recruiterName}</Box>
            )
          ) : null}
          {!hasTitle && !hasCompany && !hasLocation && !hasRecruiter ? "-" : null}
        </Typography>

        <Box sx={{ mt: 0.7, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.1 }}>
          <MatchActionButton
            disabled={!item.candidate_id || moving}
            onClick={() => onRejectCandidate?.(item.candidate_id)}
          >
            Pass
          </MatchActionButton>
          {canDelete ? (
            <MatchActionButton
              color={DANGER}
              disabled={!item.candidate_id || moving}
              onClick={() => onDeleteCandidate?.(item.candidate_id)}
            >
              Delete
            </MatchActionButton>
          ) : null}
        </Box>

        <RejectionDetails item={item} compact />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.75 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: PURPLE, whiteSpace: "nowrap" }}>
          {Math.round(Number(item.match_score || 0))}%
        </Typography>

        <Checkbox
          size="small"
          checked={checked}
          disabled={!item.candidate_id || moving}
          onChange={() => onToggleSelect?.(item.candidate_id)}
          onClick={(event) => event.stopPropagation()}
          sx={{
            p: 0,
            width: 18,
            height: 18,
            mt: 0.15,
            color: "#D0D5DD",
            "& .MuiSvgIcon-root": { fontSize: 18 },
            "&.Mui-checked": { color: PURPLE },
          }}
        />
      </Box>
    </Box>
  );
}

export default function JobDetailsMatchesTab({
  data,
  selectedFilter = "all",
  onEditHeader,
  onMoveCandidatesToStage,
  onDeleteCandidates,
  prefetchedRows = null,
  refreshKey,
  onRowsChange,
  canDeleteCandidates = false,
  currentUserRole = "",
}) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [recruiterUsers, setRecruiterUsers] = useState([]);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const [moveStageMenuState, setMoveStageMenuState] = useState({
    anchorEl: null,
    candidateIds: [],
  });
  const [rejectDialogState, setRejectDialogState] = useState({
    open: false,
    candidateIds: [],
  });
  const [moveStageSaving, setMoveStageSaving] = useState(false);
  const [deleteSaving, setDeleteSaving] = useState(false);
  const [pendingCandidateIds, setPendingCandidateIds] = useState([]);
  const onRowsChangeRef = useRef(onRowsChange);

  useEffect(() => {
    onRowsChangeRef.current = onRowsChange;
  }, [onRowsChange]);

  const publishRows = useCallback((nextRows) => {
    const normalizedRows = Array.isArray(nextRows) ? nextRows : [];
    setRows(normalizedRows);
    onRowsChangeRef.current?.(normalizedRows);
  }, []);

  const entityType =
    String(data?.entityType || "job").toLowerCase() === "lead" ? "lead" : "job";
  const entityId = Number(entityType === "lead" ? data?.leadId : data?.jobId || 0);
  const entityLabel = entityType === "lead" ? "lead" : "job";

  useEffect(() => {
    let active = true;

    fetchRecruiterUsers({ apiBase: API_BASE })
      .then((nextUsers) => {
        if (!active) return;
        setRecruiterUsers(Array.isArray(nextUsers) ? nextUsers : []);
      })
      .catch((error) => {
        if (!active) return;
        console.error("Failed to load recruiter users for matches tab:", error);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!Array.isArray(prefetchedRows)) return;
    publishRows(
      prefetchedRows
        .filter((row) => isAiMatchRow(row))
        .filter((row) => !row?.stage_moved_at)
        .map((row) => normalizeMatchRow(row))
    );
    setLoadError("");
  }, [prefetchedRows, publishRows]);

  useEffect(() => {
    if (!entityId) {
      publishRows([]);
      setLoading(false);
      setLoadError("");
      return;
    }

    let active = true;
    const controller = new AbortController();

    setLoading(true);
    setLoadError("");

    const endpoint =
      entityType === "lead"
        ? `${API_BASE}/api/leads/${encodeURIComponent(entityId)}/candidates`
        : `${API_BASE}/api/jobs/${encodeURIComponent(entityId)}/matches`;

    fetch(endpoint, {
      method: "GET",
      headers: {
        ...getAuthHeaders(),
      },
      cache: "no-store",
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorText = await response.text().catch(() => "");
          throw new Error(errorText || `Failed to load ${entityLabel} matches`);
        }
        return response.json();
      })
      .then((payload) => {
        if (!active) return;
        const nextRows = Array.isArray(payload?.rows) ? payload.rows : [];
        const normalizedRows = nextRows
          .filter((row) => isAiMatchRow(row))
          .filter((row) => !row?.stage_moved_at)
          .map((row) => normalizeMatchRow(row));
        publishRows(normalizedRows);
      })
      .catch((err) => {
        if (!active || err?.name === "AbortError") return;
        console.error(`Failed to load ${entityLabel} matches:`, err);
        setLoadError(err?.message || `Failed to load ${entityLabel} matches`);
      })
      .finally(() => {
        if (!active) return;
        setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [entityId, entityLabel, entityType, publishRows, refreshKey]);

  const recruiterUsersByName = useMemo(() => {
    const nextMap = new Map();

    (Array.isArray(recruiterUsers) ? recruiterUsers : []).forEach((user) => {
      const lookupKey = normalizeLookupName(user?.name);
      if (!lookupKey || nextMap.has(lookupKey)) return;
      nextMap.set(lookupKey, user);
    });

    return nextMap;
  }, [recruiterUsers]);

  const enrichedRows = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        recruiterProfileId: row?.recruiter_name
          ? getRecruiterProfileId(recruiterUsersByName, row.recruiter_name)
          : null,
      })),
    [recruiterUsersByName, rows]
  );

  const filteredRows = useMemo(
    () => enrichedRows.filter((row) => isJobCandidateStatMatch(row, selectedFilter)),
    [enrichedRows, selectedFilter]
  );

  const selectedCandidateIdSet = useMemo(
    () => new Set(selectedCandidateIds),
    [selectedCandidateIds]
  );
  const pendingCandidateIdSet = useMemo(
    () => new Set(pendingCandidateIds),
    [pendingCandidateIds]
  );
  const actionSaving = moveStageSaving || deleteSaving;
  const canDeleteMatchCandidates =
    Boolean(canDeleteCandidates) && typeof onDeleteCandidates === "function";

  const toggleCandidateSelection = useCallback((candidateId) => {
    const normalizedCandidateId = Number(candidateId || 0);
    if (!Number.isFinite(normalizedCandidateId) || normalizedCandidateId <= 0) return;

    setSelectedCandidateIds((prev) =>
      prev.includes(normalizedCandidateId)
        ? prev.filter((value) => value !== normalizedCandidateId)
        : [...prev, normalizedCandidateId]
    );
  }, []);

  const openBulkMoveStageMenu = useCallback(
    (event) => {
      if (actionSaving) return;
      if (typeof onMoveCandidatesToStage !== "function") return;
      if (!selectedCandidateIds.length) return;

      setMoveStageMenuState({
        anchorEl: event.currentTarget,
        candidateIds: selectedCandidateIds,
      });
    },
    [actionSaving, onMoveCandidatesToStage, selectedCandidateIds]
  );

  const closeBulkMoveStageMenu = useCallback(() => {
    if (actionSaving) return;

    setMoveStageMenuState({ anchorEl: null, candidateIds: [] });
  }, [actionSaving]);

  const openRejectDialog = useCallback(
    (candidateIds = []) => {
      if (actionSaving) return;

      const normalizedCandidateIds = Array.from(
        new Set(
          (Array.isArray(candidateIds) ? candidateIds : [candidateIds])
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );

      if (!normalizedCandidateIds.length) return;

      setMoveStageMenuState({ anchorEl: null, candidateIds: [] });
      setRejectDialogState({
        open: true,
        candidateIds: normalizedCandidateIds,
      });
    },
    [actionSaving]
  );

  const closeRejectDialog = useCallback(() => {
    if (actionSaving) return;

    setRejectDialogState({
      open: false,
      candidateIds: [],
    });
  }, [actionSaving]);

  const handleRejectCandidate = useCallback(
    (candidateId) => {
      openRejectDialog([candidateId]);
    },
    [openRejectDialog]
  );

  const handleSelectMoveStage = useCallback(
    async (nextStage, candidateIds = moveStageMenuState.candidateIds) => {
      if (moveStageSaving) return;
      if (typeof onMoveCandidatesToStage !== "function") return;

      const normalizedCandidateIds = Array.from(
        new Set(
          (Array.isArray(candidateIds) ? candidateIds : [candidateIds])
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );
      if (!normalizedCandidateIds.length) return;
      if (resolveJobCandidateDisplayStageLabel({ candidate_stage: nextStage }) === "Rejected") {
        openRejectDialog(normalizedCandidateIds);
        return;
      }

      setMoveStageSaving(true);
      setPendingCandidateIds(normalizedCandidateIds);
      try {
        await onMoveCandidatesToStage(normalizedCandidateIds, nextStage);
        setSelectedCandidateIds((prev) =>
          prev.filter((candidateId) => !normalizedCandidateIds.includes(candidateId))
        );
        setMoveStageMenuState({ anchorEl: null, candidateIds: [] });
      } catch (error) {
        console.error("Failed to move match candidates to a new stage:", error);
      } finally {
        setMoveStageSaving(false);
        setPendingCandidateIds([]);
      }
    },
    [moveStageMenuState.candidateIds, moveStageSaving, onMoveCandidatesToStage, openRejectDialog]
  );

  const handleConfirmRejectDialog = useCallback(
    async ({ rejectedBy = "", rejectedByLabel = "", reason = "", note = "", disposition, decisionBy } = {}) => {
      if (typeof onMoveCandidatesToStage !== "function") return;

      const normalizedCandidateIds = Array.from(
        new Set(
          (Array.isArray(rejectDialogState.candidateIds)
            ? rejectDialogState.candidateIds
            : []
          )
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );

      if (!normalizedCandidateIds.length) return;

      setMoveStageSaving(true);
      setPendingCandidateIds(normalizedCandidateIds);
      try {
        await onMoveCandidatesToStage(normalizedCandidateIds, "Rejected", {
          rejectedBy: rejectedBy || rejectedByLabel,
          rejectionReason: reason,
          rejectionNote: note,
          disposition, decisionBy,
        });
        setSelectedCandidateIds((prev) =>
          prev.filter((candidateId) => !normalizedCandidateIds.includes(candidateId))
        );
        setRejectDialogState({
          open: false,
          candidateIds: [],
        });
      } catch (error) {
        console.error("Failed to reject match candidates:", error);
        throw error;
      } finally {
        setMoveStageSaving(false);
        setPendingCandidateIds([]);
      }
    },
    [onMoveCandidatesToStage, rejectDialogState.candidateIds]
  );

  const handleDeleteCandidates = useCallback(
    async (candidateIds = []) => {
      if (!canDeleteMatchCandidates || actionSaving) return;

      const normalizedCandidateIds = Array.from(
        new Set(
          (Array.isArray(candidateIds) ? candidateIds : [candidateIds])
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value) && value > 0)
        )
      );
      if (!normalizedCandidateIds.length) return;

      const candidateNames = normalizedCandidateIds
        .map((candidateId) =>
          enrichedRows.find((row) => Number(row?.candidate_id || 0) === candidateId)
            ?.candidate_name
        )
        .filter(Boolean);
      const confirmed = window.confirm(
        normalizedCandidateIds.length === 1
          ? `Delete ${candidateNames[0] || "this candidate"} from this job? This removes them from the matches tab.`
          : `Delete ${normalizedCandidateIds.length} candidates from this job? This removes them from the matches tab.`
      );
      if (!confirmed) return;

      const previousRows = rows;
      setDeleteSaving(true);
      setPendingCandidateIds(normalizedCandidateIds);
      setRows((prev) =>
        prev.filter(
          (row) => !normalizedCandidateIds.includes(Number(row?.candidate_id || 0))
        )
      );

      try {
        await onDeleteCandidates(normalizedCandidateIds);
        setSelectedCandidateIds((prev) =>
          prev.filter((candidateId) => !normalizedCandidateIds.includes(candidateId))
        );
        setMoveStageMenuState({ anchorEl: null, candidateIds: [] });
      } catch (error) {
        console.error("Failed to delete match candidates from this job:", error);
        setRows(previousRows);
      } finally {
        setDeleteSaving(false);
        setPendingCandidateIds([]);
      }
    },
    [actionSaving, canDeleteMatchCandidates, enrichedRows, onDeleteCandidates, rows]
  );

  const handleDeleteCandidate = useCallback(
    async (candidateId) => {
      await handleDeleteCandidates([candidateId]);
    },
    [handleDeleteCandidates]
  );

  const handleOpenCompanyProfile = useCallback(
    (companyId, companyName = "") => {
      const normalizedCompanyId = Number(companyId || 0);
      if (!Number.isFinite(normalizedCompanyId) || normalizedCompanyId <= 0) return;

      const resolvedCompanyName = String(companyName || "").trim() || "Company";
      const payload = buildOpenCompanyNavPayload({
        company: {
          id: normalizedCompanyId,
          company_id: normalizedCompanyId,
          companyId: normalizedCompanyId,
          name: resolvedCompanyName,
          company_name: resolvedCompanyName,
        },
        defaultTab: "details",
        returnListTab: "companies",
        entityType: "company",
      });
      if (!payload) return;

      try {
        sessionStorage.setItem(NAV_CACHE_KEY, JSON.stringify(payload));
      } catch {
        // Ignore session storage failures.
      }

      navigate(
        {
          pathname: "/companies",
          search: `?companyId=${encodeURIComponent(
            String(normalizedCompanyId)
          )}&tab=details&entityType=company&returnTab=companies`,
        },
        {
          state: payload,
        }
      );
    },
    [navigate]
  );

  const handleOpenRecruiterProfile = useCallback(
    (recruiterProfileId) => {
      const normalizedProfileId = Number(recruiterProfileId || 0);
      if (!Number.isFinite(normalizedProfileId) || normalizedProfileId <= 0) return;

      navigate(`/profile/${encodeURIComponent(String(normalizedProfileId))}`);
    },
    [navigate]
  );

  const stages = useMemo(() => {
    const rowsByStage = new Map(DEFAULT_STAGE_ORDER.map((stage) => [stage, []]));
    filteredRows.forEach((row) => {
      const stage = resolveJobCandidateDisplayStageLabel(row);
      if (!rowsByStage.has(stage)) rowsByStage.set(stage, []);
      rowsByStage.get(stage).push(row);
    });

    const orderedStages = Array.from(
      new Set([...DEFAULT_STAGE_ORDER, ...rowsByStage.keys()])
    );

    return orderedStages
      .map((stage) => ({
        key: stage,
        icon: <AccordionHeadingIcon stage={stage} />,
        items: rowsByStage.get(stage) || [],
      }))
      .filter((stage) => stage.items.length > 0);
  }, [filteredRows]);

  const summaryText = useMemo(() => {
    if (loading || loadError) return "";

    const filterLabel = getJobCandidateStatLabel(selectedFilter);
    if (!rows.length) {
      return `No AI candidate matches are saved for this ${entityLabel} yet.`;
    }

    if (!filterLabel) {
      return `${rows.length} AI-ranked candidate matches saved for this ${entityLabel}.`;
    }

    return `Showing ${filteredRows.length} of ${rows.length} candidates for "${filterLabel}".`;
  }, [entityLabel, filteredRows.length, loadError, loading, rows.length, selectedFilter]);

  const defaultExpanded = useMemo(() => {
    if (!stages.length) return {};
    return { [stages[0].key]: true };
  }, [stages]);

  return (
    <Box sx={{ backgroundColor: "#fff" }}>
      <JobTabHeader
        companyLogo={data?.companyLogo}
        companyName={data?.companyName}
        companyDomain={data?.companyDomain}
        profileData={data}
        onEdit={onEditHeader}
        sx={{ mb: 1.25 }}
      />

      <Box sx={{ pt: 3, pb: 3, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
        <Button
          variant="contained"
          startIcon={<MoveStageActionIcon size={16} />}
          endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 18 }} />}
          disabled={
            actionSaving ||
            !selectedCandidateIds.length ||
            typeof onMoveCandidatesToStage !== "function"
          }
          sx={MOVE_STAGE_BULK_BUTTON_SX}
          onClick={openBulkMoveStageMenu}
        >
          Move Stage
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
          <CircularProgress size={18} />
          <Typography sx={{ fontSize: 12, color: MUTED }}>
            Loading matched candidates...
          </Typography>
        </Box>
      ) : null}

      {loadError ? (
        <Alert severity="error" sx={{ mb: 1.5 }}>
          {loadError}
        </Alert>
      ) : null}

      {!loading && !loadError ? (
        <Typography sx={{ fontSize: 12, color: MUTED, mb: 1.5 }}>
          {summaryText}
        </Typography>
      ) : null}

      <RecruitingStageAccordionList
        candidates={rows} jobId={entityType === "job" ? entityId : undefined} refreshKey={refreshKey}
        stages={stages}
        defaultExpanded={defaultExpanded}
        renderGrid={({ items }) => (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
              gap: 1.25,
            }}
          >
            {items.map((item) => (
              <MatchCard
                key={item.id || `${item.job_id}-${item.candidate_id}`}
                item={item}
                checked={!!(item.candidate_id && selectedCandidateIdSet.has(item.candidate_id))}
                moving={!!(item.candidate_id && pendingCandidateIdSet.has(item.candidate_id))}
                canDelete={canDeleteMatchCandidates}
                onToggleSelect={toggleCandidateSelection}
                onRejectCandidate={handleRejectCandidate}
                onDeleteCandidate={handleDeleteCandidate}
                onOpenCompany={handleOpenCompanyProfile}
                onOpenRecruiterProfile={handleOpenRecruiterProfile}
              />
            ))}
          </Box>
        )}
        renderList={({ items }) => (
          <Box sx={{ display: "grid", gap: 1 }}>
            {items.map((item) => (
              <MatchRow
                key={item.id || `${item.job_id}-${item.candidate_id}`}
                item={item}
                checked={!!(item.candidate_id && selectedCandidateIdSet.has(item.candidate_id))}
                moving={!!(item.candidate_id && pendingCandidateIdSet.has(item.candidate_id))}
                canDelete={canDeleteMatchCandidates}
                onToggleSelect={toggleCandidateSelection}
                onRejectCandidate={handleRejectCandidate}
                onDeleteCandidate={handleDeleteCandidate}
                onOpenCompany={handleOpenCompanyProfile}
                onOpenRecruiterProfile={handleOpenRecruiterProfile}
              />
            ))}
          </Box>
        )}
      />

      <Menu
        anchorEl={moveStageMenuState.anchorEl}
        open={Boolean(moveStageMenuState.anchorEl)}
        onClose={closeBulkMoveStageMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        MenuListProps={{ dense: true }}
      >
        {MOVE_STAGE_OPTIONS.map((stageOption) => (
          <MenuItem
            key={stageOption}
            disabled={actionSaving}
            onClick={() => handleSelectMoveStage(stageOption)}
            sx={{
              fontFamily: "Be Vietnam Pro",
              fontSize: 12,
              fontWeight: 800,
              color: TEXT,
            }}
          >
            {stageOption}
          </MenuItem>
        ))}
      </Menu>

      <RejectedStageDialog
        jobSpecific={data?.entityType !== "lead"}
        open={rejectDialogState.open}
        loading={moveStageSaving}
        candidateCount={rejectDialogState.candidateIds.length}
        currentUserRole={currentUserRole}
        onClose={closeRejectDialog}
        onSubmit={handleConfirmRejectDialog}
      />
    </Box>
  );
}
